import * as React from 'react';

/**
 * This is an HOC for use (**_only_**) with class components that use the legacy React context but wish to _also_
 * access React's modern context. In this case, you cannot add a static `contextType` (modern context) to your class
 * that already has a static `contextTypes` object (legacy context). If you do, you get this error:
 *
 * ```
 * Warning: MyClassComponent declares both contextTypes and contextType
 * static properties. The legacy contextTypes property will be ignored.
 * ```
 *
 * (For alternatives to all other scenarios, see the comments at the bottom of this file.)
 *
 * With this HOC, you can instead easily inject a prop containing the modern context value into the class component.
 * Use like so (assuming `const MyContext = React.createContext(...)`):
 *
 * ```
 * const MyClassComponentWithContext = getModernContext('myPropName', MyContext)(MyClassComponent);
 * ```
 *
 * Or via our old friend, `compose`:
 *
 * ```
 * export default compose(
 *   ...
 *   getModernContext('myPropName', MyContext),
 *   ...
 * )(MyClassComponent);
 * ```
 *
 * Then just access the context value via `this.props.myPropName` (in our example).
 *
 * @param propName {string} the name of the prop you want to assign the modern context value to
 * @param context {React.Context} the modern context object (the one you would pass to `React.useContext`)
 *
 * @return A higher-order component (HOC) that you can apply to any component. The component that's passed in will now
 *         have a prop whose value is the context value.
 *
 * Note the name is reminiscent of the `getContext` HOC that comes with Recompose, but in this case it only handle one
 * context rather than an object referencing many, which is fitting since support for modern context in class components
 * (the `contextType` static) only handles one context, while the legacy `contextTypes` handles an object referencing
 * many. If you need to access more than one context in your class component that also uses legacy context, just
 * stack a few HOCs.
 *
 * @deprecated Prefer useContext. Read more on [Confluence](https://coursera.atlassian.net/wiki/spaces/EN/pages/2950365405/Migrating+HOCs#getModernContext-easy).
 */
const getModernContext = <TName extends string, TContext>(propName: TName, context: React.Context<TContext>) => {
  return <TProps extends Record<TName, TContext>>(WrappedComponent: React.ComponentType<TProps>) =>
    (props: Omit<TProps, TName>) => {
      const value = React.useContext(context);
      const propsWithContext = {
        ...props,
        [propName]: value,
      } as TProps;
      return <WrappedComponent {...propsWithContext} />;
    };
};

export default getModernContext;

/*

So you want to add some modern context to a component that already has legacy context? This can already be done in the
case of a function component:

type Props = {
  myProp: string;
};

type ContextType = {
  myLegacyContext: string;
};

const MyComponent = ({ myProp }: Props, { myLegacyContext }: ContextType) => {
  const myModernContext = React.useContext(MyContext);
  return (
    <div>
      <p>Prop: {myProp}</p>
      <p>Legacy: {myLegacyContext}</p>
      <p>Modern: {myModernContext}</p>
    </div>
  );
};
MyComponent.contextTypes = {
  myLegacyContext: PropTypes.string,
};

And if you just want to add modern context to a class or function component that does not currently reference legacy
context, just follow the documentation: https://reactjs.org/docs/context.html

*/
